import * as React from 'react';
import * as I18n from 'i18n-js';
import * as classNames from 'classnames';
import track from 'react-tracking';

import ScrollTarget from '@reverbdotcom/commons/dist/components/scroll_target';
import HeaderDropdown from '@reverbdotcom/commons/dist/components/header_dropdown';
import NewsDropdown from './news_dropdown';
import CategoryFlyout from '../category_flyout';
import CoreLink from '@reverbdotcom/commons/dist/components/core_link';

import {
  categoryData,
  brandData,
  exploreLinks,
  LinkData,
  CategoryPanelData,
} from '../category_flyout_data';
import bind from '@reverbdotcom/commons/dist/bind';
import { TrackingProps } from '@reverbdotcom/commons/dist/tracker';
import { MParticleEventName } from '@reverbdotcom/commons/dist/elog/mparticle_tracker';

const MOBILE_WEB_BREAKPOINT = 640;
interface IState {
  openFlyoutKey: string;
  activeDropdownKey: string;
  overflowCategories: boolean;
}

function ViewMoreButton() {
  return (
    <button className="category-flyout-header__view-more" type="button">
      {I18n.t('discovery.categoryNavBar.viewAll')}
    </button>
  );
}

function ExploreLink(linkData: LinkData) {
  return (
    <CoreLink
      key={linkData.textKey}
      className="category-flyout-header__link"
      to={linkData.href}
      entityId={linkData.href}
    >
      {I18n.t(`discovery.categoryNavBar.exploreLinks.${linkData.textKey}`)}
    </CoreLink>
  );
}


@track({
  mParticleInteraction: (event) => ({
    eventName: MParticleEventName.NavBarInteraction,
    entityId: event.entityId,
  }),
})
export class CategoryFlyoutHeader extends React.Component<TrackingProps, IState> {
  state = {
    openFlyoutKey: null,
    activeDropdownKey: null,
    overflowCategories: false,
  };

  scrollTarget: ScrollTarget;
  categoriesInner: HTMLElement = null;

  componentDidMount() {
    window.addEventListener('resize', this.updateOverflow);
    this.updateOverflow();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateOverflow);
  }

  setCategoryFlyout(openFlyoutKey) {
    if (openFlyoutKey === this.state.openFlyoutKey) {
      return this.setState({ openFlyoutKey: null });
    }

    if (openFlyoutKey) {
      const shouldScroll = window && window.innerWidth < MOBILE_WEB_BREAKPOINT;

      if (shouldScroll) this.scrollTarget.scrollToTop();

      this.props.tracking.trackEvent({
        entityId: openFlyoutKey,
      });
    }

    return this.setState({ openFlyoutKey });
  }

  @bind
  closeFlyout() {
    this.setState({ openFlyoutKey: null });
  }

  @bind
  setDropdownOpen(key) {
    this.setState({ activeDropdownKey: key });
  }

  @bind
  updateOverflow() {
    if (!window.document || !this.categoriesInner) {
      this.setState({ overflowCategories: false });
      return;
    }
    const selector = '.category-flyout-header__link-bar__primary button:last-of-type';
    const rowCutoff = this.categoriesInner.getBoundingClientRect().right;
    const el = this.categoriesInner.querySelector(selector);

    const overflowCategories = !!el && el.getBoundingClientRect().right > rowCutoff;

    this.setState(
      { overflowCategories },
    );
  }

  renderFlyoutButton(category: CategoryPanelData) {
    const classes = classNames(
      'category-flyout-header__link',
      { 'category-flyout-header__link--active': category.key === this.state.openFlyoutKey },
    );

    return (
      <button
        key={category.key}
        type="button"
        className={classes}
        data-header-category={category.key}
        onClick={() => this.setCategoryFlyout(category.key)}
      >
        {I18n.t(`${category.i18nPrefix}.title`)}
      </button>
    );
  }

  renderSecondary() {
    return (
      <div className="category-flyout-header__link-bar__secondary">
        {this.renderFlyoutButton(brandData)}
        <div className="d-inline-block" data-news-trigger>
          <HeaderDropdown
            trigger={
              <CoreLink
                to="/news"
                className="icon-r-caret-down category-flyout-header__link"
                entityId="news"
              >
                {I18n.t('discovery.navLinks.news')}
              </CoreLink>
            }
            contents={<NewsDropdown />}
            onOpen={this.setDropdownOpen}
            dropdownKey="news"
            activeDropdownKey={this.state.activeDropdownKey}
          />
        </div>
        <div className="d-inline-block">
          <HeaderDropdown
            trigger={
              <span
                className="icon-r-caret-down category-flyout-header__link"
              >
                {I18n.t('discovery.navLinks.explore')}
              </span>
            }
            contents={exploreLinks.map(ExploreLink)}
            onOpen={this.setDropdownOpen}
            dropdownKey="explore"
            activeDropdownKey={this.state.activeDropdownKey}
          />
        </div>
        <CoreLink
          key="/shops"
          className="category-flyout-header__link"
          to="shops"
          entityId="shops"
        >
          {I18n.t('discovery.categoryNavBar.shops')}
        </CoreLink>
        <CoreLink
          to="/page/reverb-gives"
          className="category-flyout-header__link"
          entityId="reverb-gives"
        >
          {I18n.t('discovery.navLinks.reverbGives')}
        </CoreLink>
      </div>
    );
  }

  renderOverflow() {
    if (!this.state.overflowCategories) { return null; }

    const buttons = categoryData.map((category) => {
      return this.renderFlyoutButton(category);
    });

    return (
      <HeaderDropdown
        trigger={<ViewMoreButton />}
        contents={buttons}
        onOpen={this.setDropdownOpen}
        dropdownKey="more"
        activeDropdownKey={this.state.activeDropdownKey}
      />
    );
  }

  render() {
    const classes = classNames(
      'category-flyout-header',
      { 'category-flyout-header--open': !!this.state.openFlyoutKey },
    );

    return (
      <div className={classes}>
        <div
          className="category-flyout-header__underlay"
          onClick={this.closeFlyout}
          role="presentation"
        />
        <div className="category-flyout-header__link-bar">
          <ScrollTarget ref={(r) => { this.scrollTarget = r; }} />
          <div
            className="category-flyout-header__link-bar__primary"
            ref={(el) => { this.categoriesInner = el; }}
          >
            {categoryData.map((category) => {
              return this.renderFlyoutButton(category);
            })}
            <div className="d-none tablet-d-inline-block">
              {this.renderFlyoutButton(brandData)}
            </div>
          </div>
          {this.renderOverflow()}
          {this.renderSecondary()}
        </div>
        {categoryData.map((category) => (
          <CategoryFlyout
            key={category.key}
            data={category}
            visible={category.key === this.state.openFlyoutKey}
            closeDialog={this.closeFlyout}
          />
        ))}
        <CategoryFlyout
          key={brandData.key}
          data={brandData}
          visible={brandData.key === this.state.openFlyoutKey}
          closeDialog={this.closeFlyout}
        />
      </div>
    );
  }
}

export default CategoryFlyoutHeader;



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/new_header/category_flyout_header.tsx